import {
  Backdrop,
  Button,
  Divider,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { getMachineThunk } from "store/reducers/vending";
import { LazyLoadImage } from "react-lazy-load-image-component";
import addURLPrefix from "utils/addURLPrefix";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditBasicMachineDetails from "pages/vending/machine/operations/EditBasicMachineDetails";
import AddMoreCabins from "pages/vending/machine/operations/AddMoreCabins";
import { useDropzone } from "react-dropzone";
import {
  createMachinePhotosThunk,
  deleteMachinePhotoThunk,
} from "store/reducers/machine_func/machinePhotos";
import { LoadingButton } from "@mui/lab";
import SimpleBarHorizontal from "components/SimpleBarHorizontal";
import AssignMachineAdminDialog from "pages/vending/machine/operations/AssignMachineAdminDialog";
import AssignedAdmins from "pages/vending/machine/detail/AssignedAdmins";
import AssignedPayments from "pages/vending/machine/detail/AssignedPayments";
import { deletePaymentsForMachineThunk } from "store/reducers/payments";
import AssignPaymentsDialog from "pages/vending/machine/operations/AssignPaymentsDialog";
import AssignedFillingPersons from "./AssignedFillingPersons";
import AssignFillingPersonDialog from "../operations/AssignFillingPersonDialog";
import { Update } from "@mui/icons-material";
import UpdatePaymentAssignment from "../operations/UpdatePaymentAssignment";

const MachineDetails = () => {
  const vendingState = useSelector((state) => state.vending);
  const dispatch = useDispatch();
  const { m_id } = useParams();
  useEffect(() => {
    dispatch(getMachineThunk(m_id));
  }, [dispatch, m_id]);

  const [machine, setMachine] = React.useState(null);
  const [backDropImage, setBackDropImage] = React.useState(null);
  const [openBackDrop, setOpenBackDrop] = React.useState(false);

  const onImageClick = (image) => {
    setBackDropImage(image);
    setOpenBackDrop(true);
  };
  const handleBackdropClose = () => {
    setOpenBackDrop(false);
  };
  const [basicDetailsOpen, setBasicDetailsOpen] = React.useState(false);
  const handleBasicDetailsOpen = () => {
    setBasicDetailsOpen(false);
  };

  const [addMoreCabinsOpen, setAddMoreCabinsOpen] = React.useState(false);
  const handleAddMoreCabinsOpen = () => {
    setAddMoreCabinsOpen(false);
  };

  const [machineAdminOpen, setMachineAdminOpen] = React.useState(false);

  const handleMachineAdminClose = () => {
    setMachineAdminOpen(false);
  };

  const [fillingPersonOpen, setFillingPersonOpen] = React.useState(false);

  const handleFillingPersonClose = () => {
    setFillingPersonOpen(false);
  };

  const [machinePaymentOpen, setMachinePaymentOpen] = React.useState(false);

  const handleMachinePaymentClose = () => {
    setMachinePaymentOpen(false);
  };

  useEffect(() => {
    const mc = vendingState.machines.find(
      (machine) => machine.id === parseInt(m_id, 0)
    );
    setMachine(mc);
  }, [m_id, vendingState.machines]);

  const { acceptedFiles, getRootProps, getInputProps } = useDropzone({
    maxFiles: 5,
    accept: {
      "image/png": [".png"],
      "image/jpeg": [".jpg", ".jpeg"],
    },
  });

  useEffect(() => {
    if (acceptedFiles.length > 0) {
      acceptedFiles.forEach((file) => {
        dispatch(
          createMachinePhotosThunk({ photo: file, machine: machine.id })
        );
      });
    }
  }, [acceptedFiles, dispatch, machine?.id]);

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [currentImage, setCurrentImage] = React.useState(null);
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const handleMenuClick = (event, item) => {
    setAnchorEl(event.currentTarget);
    setCurrentImage(item);
  };

  const [anchorElPayment, setAnchorElPayment] = React.useState(null);
  const [selectedPayment, setSelectedPayment] = React.useState(null);
  const handlePaymentMenuClose = () => {
    setAnchorElPayment(null);
  };
  const handlePaymentMenuClick = (event, item) => {
    setAnchorElPayment(event.currentTarget);
    setSelectedPayment(item);
  };

  const deleteMenu = (onClickFunc, menuClose, anchor) => (
    <Menu
      anchorEl={anchor}
      open={anchor !== null}
      onClose={menuClose}
      onClick={menuClose}
      transformOrigin={{ horizontal: "right", vertical: "top" }}
      anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
    >
      <MenuItem onClick={onClickFunc}>Delete</MenuItem>
    </Menu>
  );

  const photoMenu = () =>
    deleteMenu(
      () => dispatch(deleteMachinePhotoThunk(currentImage)),
      handleMenuClose,
      anchorEl
    );

    const deletePayment = () =>
        dispatch(
          deletePaymentsForMachineThunk({
            machine: machine?.id,
            payment: selectedPayment?.id,
          })
        )

    const [showAssignedPaymentsUpdate, setShowAssignedPaymentsUpdate] = React.useState(false);

  const paymentMenu = () => (
    <Menu
      anchorEl={anchorElPayment}
      open={anchorElPayment !== null}
      onClose={handlePaymentMenuClose}
      onClick={handlePaymentMenuClose}
      transformOrigin={{ horizontal: "right", vertical: "top" }}
      anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
    >
      {
        (selectedPayment && selectedPayment.credential_format.length > 0) && (
          <MenuItem onClick={()=>{
            setShowAssignedPaymentsUpdate(true);
          }}>Edit configuration</MenuItem>
        )
      }
      <MenuItem onClick={deletePayment} sx={{color:"red"}}>Delete</MenuItem>
    </Menu>
  );

  return (
    <Stack direction="column" padding={2}>
      <Grid container columnSpacing={2} rowSpacing={0}>
        <Grid item xs={12} md={6} lg={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography color="text.secondary" variant="h5">
              Basic details
            </Typography>
            <Button
              size="small"
              variant="outlined"
              sx={{ marginLeft: 2, width: "12ch" }}
              onClick={() => {
                setBasicDetailsOpen(true);
              }}
            >
              Edit
            </Button>
          </Stack>
        </Grid>
      </Grid>

      <Stack direction="row" alignItems="center" sx={{ marginTop: 2 }}>
        <Typography color="text.secondary" variant="body1">
          Name :
        </Typography>
        <Typography color="text.primary" variant="body1" sx={{ marginLeft: 1 }}>
          {vendingState.updateMachineLoading ? (
            <Skeleton sx={{ width: 120 }} animation="wave" />
          ) : (
            machine?.name
          )}
        </Typography>
      </Stack>
      <Stack direction="row" alignItems="center">
        <Typography color="text.secondary" variant="body1">
          Desc :
        </Typography>
        <Typography color="text.primary" variant="body1" sx={{ marginLeft: 1 }}>
          {vendingState.updateMachineLoading ? (
            <Skeleton sx={{ width: 120 }} animation="wave" />
          ) : machine?.desc ? (
            machine?.desc
          ) : (
            "None"
          )}
        </Typography>
      </Stack>
      <Divider sx={{ marginTop: 1 }} />

      <Grid container columnSpacing={2} rowSpacing={0}>
        <Grid item xs={12} md={6} lg={4}>
          <Stack
            direction="row"
            alignItems="center"
            sx={{ marginTop: 1 }}
            justifyContent="space-between"
          >
            <Typography color="text.secondary" variant="h5">
              Photos
            </Typography>
            <LoadingButton
              size="small"
              variant="outlined"
              sx={{ marginLeft: 2, width: "12ch" }}
              {...getRootProps({ className: "dropzone" })}
              loading={vendingState.createMachinePhotosLoading[machine?.id]}
            >
              <input {...getInputProps()} />
              Add more
            </LoadingButton>
          </Stack>
        </Grid>
      </Grid>

      <SimpleBarHorizontal style={{ maxWidth: "100%", display: "flex" }}>
        <Stack
          direction="row"
          alignItems="center"
          sx={{ marginTop: 1, marginBottom: 2 }}
        >
          {machine?.photos.map((item) => (
            <Paper
              key={item.id}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: 0.5,
                position: "relative",
                marginLeft: 1,
              }}
            >
              {vendingState.deleteMachinePhotosLoading[item.id] ? (
                <Skeleton
                  variant="rectangular"
                  sx={{ height: 200, width: 200 }}
                  animation="wave"
                />
              ) : (
                <>
                  <LazyLoadImage
                    alt="machine image"
                    src={addURLPrefix(item.photo)}
                    height={200}
                    width={200}
                    style={{ objectFit: "cover", borderRadius: 4 }}
                    onClick={() => onImageClick(addURLPrefix(item.photo))}
                  />
                  <IconButton
                    aria-label="settings"
                    sx={{ position: "absolute", top: 2, right: 2 }}
                    onClick={(e) => handleMenuClick(e, item)}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </>
              )}
            </Paper>
          ))}
        </Stack>
      </SimpleBarHorizontal>
      <Divider sx={{ marginTop: 1 }} />
      <Grid container columnSpacing={2} rowSpacing={0}>
        <Grid item xs={12} md={6} lg={4}>
          <Stack
            direction="row"
            alignItems="center"
            sx={{ marginTop: 1 }}
            justifyContent="space-between"
          >
            <Stack direction="row" alignItems="center" sx={{ marginTop: 1 }}>
              <Typography color="text.secondary" variant="h6">
                Current location :
              </Typography>
              <Typography
                color="text.secondary"
                variant="h6"
                sx={{ marginLeft: 2 }}
              >
                {machine?.location ? machine.location : "Not set"}
              </Typography>
            </Stack>

            <Button
              size="small"
              variant="outlined"
              sx={{ marginLeft: 2, width: "12ch" }}
            >
              {machine?.location ? "Change" : "Assign"}
            </Button>
          </Stack>
        </Grid>
      </Grid>

      <Divider sx={{ marginTop: 1 }} />
      <Grid container columnSpacing={2} rowSpacing={0}>
        <Grid item xs={12} md={6} lg={4}>
          <Stack
            direction="row"
            alignItems="center"
            sx={{ marginTop: 2 }}
            justifyContent="space-between"
          >
            <Typography color="text.secondary" variant="h5">
              Machine admins
            </Typography>
            <Button
              size="small"
              variant="outlined"
              sx={{ marginLeft: 2, width: "12ch" }}
              onClick={() => setMachineAdminOpen(true)}
            >
              Add more
            </Button>
          </Stack>
        </Grid>
      </Grid>
      <Stack direction="row" alignItems="center" sx={{ marginTop: 2 }}>
        {machine && (
          <AssignedAdmins
            machine_admins={machine?.admins}
            machine_id={machine?.id}
          />
        )}
      </Stack>

      <Divider sx={{ marginTop: 1 }} />
      <Grid container columnSpacing={2} rowSpacing={0}>
        <Grid item xs={12} md={6} lg={4}>
          <Stack
            direction="row"
            alignItems="center"
            sx={{ marginTop: 2 }}
            justifyContent="space-between"
          >
            <Typography color="text.secondary" variant="h5">
              Filling persons
            </Typography>
            <Button
              size="small"
              variant="outlined"
              sx={{ marginLeft: 2, width: "12ch" }}
              onClick={() => setFillingPersonOpen(true)}
            >
              Add more
            </Button>
          </Stack>
        </Grid>
      </Grid>

      <Stack direction="row" alignItems="center" sx={{ marginTop: 2 }}>
        {machine && (
          <AssignedFillingPersons
            filling_persons={machine?.filling_persons}
            machine_id={machine?.id}
          />
        )}
      </Stack>

      <Divider sx={{ marginTop: 1 }} />
      <Grid container columnSpacing={2} rowSpacing={0}>
        <Grid item xs={12} md={6} lg={4}>
          <Stack
            direction="row"
            alignItems="center"
            sx={{ marginTop: 2 }}
            justifyContent="space-between"
          >
            <Typography color="text.secondary" variant="h5">
              Payments list
            </Typography>
            <Button
              size="small"
              variant="outlined"
              sx={{ marginLeft: 2, width: "12ch" }}
              onClick={() => setMachinePaymentOpen(true)}
            >
              Add more
            </Button>
          </Stack>
        </Grid>
      </Grid>

      <AssignedPayments
        machine={machine}
        handlePaymentMenuClick={handlePaymentMenuClick}
      />
      <Divider sx={{ marginTop: 1 }} />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={openBackDrop}
        onClick={handleBackdropClose}
      >
        <LazyLoadImage
          alt="machine image"
          src={backDropImage}
          height="95%"
          width="95%"
          style={{ objectFit: "contain" }}
        />
      </Backdrop>
      {machine && (
        <>
          <EditBasicMachineDetails
            open={basicDetailsOpen}
            handleClose={handleBasicDetailsOpen}
            machine={machine}
          />
          <AddMoreCabins
            open={addMoreCabinsOpen}
            handleClose={handleAddMoreCabinsOpen}
            machine={machine}
          />
          <AssignMachineAdminDialog
            open={machineAdminOpen}
            handleClose={handleMachineAdminClose}
            machine_id={machine.id}
          />
          <AssignFillingPersonDialog
            open={fillingPersonOpen}
            handleClose={handleFillingPersonClose}
            machine_id={machine.id}
          />
          <AssignPaymentsDialog
            open={machinePaymentOpen}
            handleClose={handleMachinePaymentClose}
            machine={machine}
          />
        </>
      )}
      {showAssignedPaymentsUpdate && (
        <UpdatePaymentAssignment
          open={showAssignedPaymentsUpdate}
          handleClose={() => setShowAssignedPaymentsUpdate(false)}
          machine={machine}
          payment={selectedPayment}
        />
      )}
      {photoMenu()}
      {paymentMenu()}
    </Stack>
  );
};

export default MachineDetails;
